// Mimique logo, ported from the "Mimique Final Logo" Claude Design file.
// The mark is a squared listen/repeat loop with two arrowheads and an M at the
// centre, drawn from the same maths as the design so it stays identical at any
// size. `simple` drops the ripple rings and enlarges the M, which is what keeps
// it legible at favicon sizes.

// Two greens on purpose, as in the original logo file: `mint` for the wordmark
// and mark on dark backgrounds, where the darker green goes muddy, and
// `emeraldLight` (the app's own accent token) on light backgrounds. CTAs use
// the app accent, never mint.
const BRAND = {
    emerald: '#2d7a4f',
    emeraldDark: '#1a5235',
    emeraldLight: '#4da673',
    textLight: '#e0f0e8',
    mint: '#7fe0a3',
    ink: '#0a1610',
};

// Superellipse point: squareness 0 = circle, higher = squarer.
const pointAt = (deg, R, squareness, cx = 40, cy = 40) => {
    const a = (deg * Math.PI) / 180;
    if (!squareness) return [cx + R * Math.cos(a), cy - R * Math.sin(a)];
    const c = Math.cos(a);
    const s = Math.sin(a);
    const ex = 2 / squareness;
    const x = Math.sign(c) * Math.pow(Math.abs(c), ex);
    const y = Math.sign(s) * Math.pow(Math.abs(s), ex);
    return [cx + R * x, cy - R * y];
};

const superPath = (r, squareness, cx = 40, cy = 40, steps = 48) => {
    let d = '';
    for (let i = 0; i <= steps; i++) {
        const [x, y] = pointAt((i * 360) / steps, r, squareness, cx, cy);
        d += `${i === 0 ? 'M' : 'L'} ${x} ${y} `;
    }
    return `${d}Z`;
};

const Loop = ({ c1, c2, sw, R = 20, squareness = 0, gapClose = 0 }) => {
    const lineW = Math.max(sw * 0.58, 1.5);
    const P = (deg) => pointAt(deg, R, squareness);

    const tStart = 165 + gapClose;
    const tEnd = 20 - gapClose;
    const bStart = 345 + gapClose;
    const bEnd = 200 - gapClose;

    const arrow = (deg, col) => {
        const [px, py] = P(deg);
        let dx;
        let dy;
        if (!squareness) {
            dx = Math.sin((deg * Math.PI) / 180);
            dy = Math.cos((deg * Math.PI) / 180);
        } else {
            const eps = 2;
            const [ax, ay] = P(deg - eps);
            const [bx, by] = P(deg + eps);
            const len = Math.hypot(bx - ax, by - ay) || 1;
            dx = -(bx - ax) / len;
            dy = -(by - ay) / len;
        }
        const rot = (-Math.atan2(dx, dy) * 180) / Math.PI;
        const T = lineW * 3.0 + 3.4;
        const Wn = lineW * 1.05 + 1.3;
        return (
            <path
                transform={`rotate(${rot} ${px} ${py})`}
                d={`M ${px} ${py + T} L ${px - Wn} ${py - lineW * 0.4} L ${px + Wn} ${py - lineW * 0.4} Z`}
                fill={col}
            />
        );
    };

    const arc = (deg0, deg1, col) => {
        const steps = 24;
        const pts = [];
        for (let i = 0; i <= steps; i++) pts.push(P(deg0 + (deg1 - deg0) * (i / steps)));
        const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');
        return <path d={d} fill="none" stroke={col} strokeWidth={lineW} strokeLinecap="round" strokeLinejoin="round" />;
    };

    return (
        <g>
            {arc(tStart, tEnd, c1)}
            {arc(bStart, bEnd, c2)}
            {arrow(tEnd, c1)}
            {arrow(bEnd, c2)}
        </g>
    );
};

const Mark = ({ c1, c2, simple }) => {
    const rings = [];
    if (!simple) {
        const ringCount = 2;
        const ringGap = 8;
        const ringStart = 11;
        const rw = Math.max(5 * 0.55, 1.3);
        for (let i = 0; i < ringCount; i++) {
            const r = ringStart + i * ringGap;
            const op = Math.max(0.9 - i * (0.35 / Math.max(ringCount - 1, 1)), 0.5);
            rings.push(<path key={i} d={superPath(r, 6)} fill="none" stroke={c2} strokeWidth={rw} opacity={op} />);
        }
    }
    return (
        <g>
            <text
                x="40"
                y="40"
                textAnchor="middle"
                dominantBaseline="central"
                fontFamily="'Nunito Sans', sans-serif"
                fontWeight={simple ? 800 : 600}
                fontSize={simple ? 32 : 19}
                fill={c1}
            >
                M
            </text>
            {rings}
            <Loop c1={c1} c2={c2} sw={simple ? 6 : 5} R={26} squareness={6} gapClose={12} />
        </g>
    );
};

/**
 * The Mimique icon.
 * `variant`: 'dark' (rounded green square, for light backgrounds or app icons)
 *            | 'bare' (no square, inherits whatever is behind it)
 */
export const MimiqueIcon = ({ size = 40, variant = 'square', onLight = false, simple = false, style, className }) => {
    const gradientId = `mqShell${onLight ? 'L' : 'D'}`;
    const c1 = variant === 'bare' && onLight ? BRAND.emeraldDark : '#ffffff';
    const c2 = variant === 'bare' && onLight ? BRAND.emeraldLight : BRAND.mint;

    if (variant === 'bare') {
        return (
            <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className} aria-hidden="true">
                <Mark c1={c1} c2={c2} simple={simple} />
            </svg>
        );
    }
    return (
        <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className} aria-hidden="true">
            <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={BRAND.emerald} />
                    <stop offset="100%" stopColor={BRAND.emeraldDark} />
                </linearGradient>
            </defs>
            <rect width="80" height="80" rx="18.4" fill={`url(#${gradientId})`} />
            <Mark c1="#ffffff" c2={BRAND.mint} simple={simple} />
        </svg>
    );
};

/** Wordmark only: "Mim" in colorA, "ique" in colorB. */
export const MimiqueWordmark = ({ colorA, colorB, fontSize = 26 }) => (
    <span
        style={{
            fontFamily: "'Nunito Sans', sans-serif",
            fontWeight: 700,
            fontSize,
            letterSpacing: '-0.015em',
            lineHeight: 1,
            whiteSpace: 'nowrap',
            userSelect: 'none',
        }}
    >
        <span style={{ color: colorA }}>Mim</span>
        <span style={{ color: colorB }}>ique</span>
    </span>
);

/**
 * Icon + wordmark lockup.
 * `on`: 'dark' | 'green' | 'light' — picks the approved colour pairing.
 */
const LOCKUP_COLOURS = {
    dark: { wA: BRAND.textLight, wB: BRAND.mint },
    green: { wA: '#ffffff', wB: BRAND.mint },
    light: { wA: BRAND.emeraldDark, wB: BRAND.emeraldLight },
};

export const MimiqueLockup = ({ on = 'dark', iconSize = 44, fontSize = 26, variant = 'square', gap = 12 }) => {
    const { wA, wB } = LOCKUP_COLOURS[on] || LOCKUP_COLOURS.dark;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap }}>
            <MimiqueIcon size={iconSize} variant={variant} onLight={on === 'light'} />
            <MimiqueWordmark colorA={wA} colorB={wB} fontSize={fontSize} />
        </span>
    );
};

export { BRAND };
